import React, {FC, useEffect, useMemo, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import useCommon from "../../../common/hooks/useCommon";
import {centerTruncate, hexToAddress, hexToUtf8} from "../../../common/utils/function";
import BTooltip from "../../../components/b-tooltip";
import BCopy from "../../../components/b-copy";
import {useTranslation} from "react-i18next";
import {ButtonGroup, Dropdown, DropdownButton, Form, ToggleButton} from 'react-bootstrap';

const TransactionLogs: FC<any> = (props) => {
    const {get0x} = useCommon();
    const {t, i18n} = useTranslation();

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isMobile, setIsMobile] = useState(windowWidth <= 990);
    const [selectedTopic, setSelectedTopic] = useState<any>({});
    const location = useLocation();
    const hashSplit = location.hash.split("#F");
    const hashIndex = isNaN(Number(hashSplit?.[1])) ? -1 : Number(hashSplit?.[1]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            setIsMobile(window.innerWidth <= 990);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const handleSelect = (eventKey: any, index: any, index1: any) => {
        setSelectedTopic({
            ...selectedTopic,
            [index.toString() + index1.toString()]: eventKey,
        });
    };

    const [selectedOptions, setSelectedOptions] = useState<{ [key: number]: 'dec' | 'hex' }>({});

    const handleOptionChange = (index: number, value: 'dec' | 'hex'): void => {
        setSelectedOptions((prev) => ({
            ...prev,
            [index]: value,
        }));
    };

    useEffect(() => {
        if (hashIndex > 0) {
            setTimeout(() => {
                const element = document.getElementById(`F${hashIndex}`);
                if (element) {
                    element.scrollIntoView({behavior: 'smooth', block: 'start'});
                }
            }, 1000);
        }
    }, [hashIndex]);

    const stats = useMemo(() => {
        return props?.transactionLogs;
    }, [props?.transactionLogs]);

    const convertHexFunction = (hex: string, convertType: string) => {
        switch (convertType) {
            case "hex":
                return hex;
            case "number":
                return BigInt(hex).toString();
            case "text":
                return hexToUtf8(hex);
            case "address":
                return hexToAddress(hex);
            default:
                return hex;
        }
    };

    function getTagName(metadata?: any): string | undefined {
        if (!metadata || !metadata.tags || metadata.tags.length === 0) return undefined;
        const nameTag = metadata.tags.find((tag: any) => tag.tagType === "name");
        return nameTag?.name;
    }

    return (
        <React.Fragment>
            <div className="bs-card mb-3">
                <div className="bs-card-body py-3 px-4">
                    <div className="bs-list">
                        {stats?.length > 0 &&
                            stats?.map((ele: any, index: number) => (
                                <div key={index} id={`F${ele?.index}`}>
                                    <div className="d-flex ps-1 py-2">
                                        <div className="mt-1 me-3">
                                            <span
                                                className="bg-success bg-opacity-10 no-border text-success fs-70x content-center rounded-pill mx-auto"
                                                style={{width: "2.8rem", height: "2.8rem"}}
                                            >
                                                {ele?.index}
                                            </span>
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden">
                                            <dl className="row align-items-baseline mb-3">
                                                <dt className="col-md-2 col-lg-1 text-md-end">
                                                    <h6 className="fw-bold mb-0">
                                                        {t("Address")}
                                                    </h6>
                                                </dt>
                                                <dd className="col-md-10 mb-0">
                                                    <a href={`/address/${get0x(ele?.address?.hash)}`} className="me-2">
                                                        <BTooltip
                                                            text={
                                                                getTagName(ele?.address?.metadata) || ele?.address?.name || ele?.address?.hash || ""
                                                            }
                                                        >
                                                            <span>
                                                                {getTagName(ele?.address?.metadata) || ele?.address?.name || ele?.address?.hash || ""}
                                                            </span>
                                                        </BTooltip>
                                                    </a>
                                                    {ele?.address?.hash ? (
                                                        <BCopy
                                                            value={ele?.address?.hash}
                                                            tooltip={`${t("Address to clipboard")}`}
                                                        />
                                                    ) : (
                                                        ""
                                                    )}
                                                    <span className="h_link ms-2">
                                                        <BCopy
                                                            value={window.location.origin + window.location.pathname + `#logs#F${ele?.index}`} icon="bi-link"
                                                            tooltip={`${t("permalink to clipboard")}`}
                                                        />
                                                    </span>
                                                </dd>
                                            </dl>
                                            {ele?.decoded ? (<dl className="row align-items-baseline mb-3">
                                                <dt className="col-md-2 col-lg-1 text-md-end">
                                                    <h6 className="mb-0 text-secondary">{t("Name")}</h6>
                                                </dt>
                                                <dd className="col-md-10 mb-0">
                                                    {ele?.decoded ? (
                                                        <React.Fragment>
                                                            <p className="text-muted font-monospace mb-0">
                                                                {ele?.decoded.method_call.split('(')[0]} {"("}
                                                                {ele?.decoded.parameters.map((param: any, index: number) => {
                                                                    let index_ct = index + 1;
                                                                    const indexedPrefix = param.indexed ? `index_topic_${index_ct} ` : '';
                                                                    return (
                                                                        <span key={index}>
                                                                            {indexedPrefix}
                                                                            <span className="text-success">{param.type}</span> {" "}
                                                                            <span className="text-danger">{param.name}</span>
                                                                            {index < ele?.decoded.parameters.length - 1 && ', '}
                                                                        </span>
                                                                    );
                                                                })}
                                                                {")"}
                                                            </p>
                                                        </React.Fragment>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </dd>
                                            </dl>) : (
                                                null
                                            )}
                                            <dl className="row align-items-baseline mb-3">
                                                <dt className="col-md-2 col-lg-1 text-md-end">
                                                    <h6 className="mb-0 text-secondary">{t("Topics")}</h6>
                                                </dt>
                                                <dd className="col-md-10 mb-0">
                                                    <div>
                                                        {ele?.topics?.length > 0 &&
                                                            ele?.topics?.map((topic: any, index1: number) => {
                                                                const paramNames = ele?.decoded?.parameters?.map((param: any) => param.name) || [];
                                                                return (
                                                                    topic && (
                                                                        <div className="bs-log-topic" key={`${topic}-${index1}`}>
                                                                            <button
                                                                                type="button"
                                                                                data-active=""
                                                                                className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap py-1.5 px-2 me-2"
                                                                            >
                                                                                {`${index1} ${
                                                                                    index1 === 1
                                                                                        ? `: ${paramNames[0] || t("from")}`
                                                                                        : index1 === 2
                                                                                            ? `: ${paramNames[1] || t("to")}`
                                                                                            : ""
                                                                                }`}
                                                                            </button>

                                                                            <div className="bs-log-margin">
                                                                                {index1 !== 0 && (
                                                                                    <div className="bs-log-arrow">
                                                                                        <DropdownButton
                                                                                            id={`dropdown-basic-button-${index1}`}
                                                                                            title={t(selectedTopic[index.toString() + index1.toString()] || "Hex")}
                                                                                            onSelect={(eventKey) =>
                                                                                                handleSelect(
                                                                                                    eventKey,
                                                                                                    index,
                                                                                                    index1
                                                                                                )
                                                                                            }
                                                                                            className="d-flex bs_token-social"
                                                                                            variant="white" size="sm"
                                                                                        >
                                                                                            <Dropdown.Item eventKey="Hex">
                                                                                                {t("Hex")}
                                                                                            </Dropdown.Item>
                                                                                            <Dropdown.Item eventKey="Dec">
                                                                                                {t("Decode")}
                                                                                            </Dropdown.Item>
                                                                                        </DropdownButton>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div className="bs-log-list-topic">
                                                                                <div className="bs-list">
                                                                                    <i className="bi bi-arrow-right text-muted mx-1"></i>
                                                                                    <span className="bs-list-content">
                                                                                        {index1 !== 0 &&
                                                                                        selectedTopic[
                                                                                        index.toString() +
                                                                                        index1.toString()
                                                                                            ] === "Dec" ? (
                                                                                            <Link
                                                                                                to={`/address/${get0x(topic)}`}
                                                                                            >
                                                                                                {isMobile
                                                                                                    ? centerTruncate(
                                                                                                        convertHexFunction(
                                                                                                            topic,
                                                                                                            "address"
                                                                                                        )
                                                                                                    )
                                                                                                    : convertHexFunction(
                                                                                                        topic,
                                                                                                        "address"
                                                                                                    )}
                                                                                            </Link>
                                                                                        ) : (
                                                                                            <React.Fragment>
                                                                                                {isMobile
                                                                                                    ? centerTruncate(
                                                                                                        convertHexFunction(
                                                                                                            topic,
                                                                                                            "hex"
                                                                                                        )
                                                                                                    )
                                                                                                    : convertHexFunction(
                                                                                                        topic,
                                                                                                        "hex"
                                                                                                    )}
                                                                                            </React.Fragment>
                                                                                        )}
                                                                                    </span>
                                                                                    {index1 !== 0 &&
                                                                                        selectedTopic[
                                                                                        index.toString() +
                                                                                        index1.toString()
                                                                                            ] === "Dec" && (
                                                                                            <BCopy
                                                                                                value={topic}
                                                                                                tooltip={`${t("to clipboard")}`}
                                                                                            />
                                                                                        )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                );
                                                            })}
                                                    </div>
                                                </dd>
                                            </dl>
                                            <dl className="row align-items-baseline mb-0">
                                                <dt className="col-md-2 col-lg-1 text-md-end">
                                                    <h6 className="font-italic mb-0 text-secondary">
                                                        {t("Data")}
                                                    </h6>
                                                </dt>
                                                <dd className="col-md-10 mb-0 bs-list-log-data">
                                                    <ul className="list-unstyled bg-light rounded p-3 mb-2 d-md-flex flex-column">
                                                        {selectedOptions[index] === 'dec' || !selectedOptions[index] ? (
                                                            ele?.decoded?.parameters
                                                                .filter((param: any) => !param.indexed)
                                                                .map((param: any, idx: number) => (
                                                                    <div className="d-flex align-items-center mb-3" key={`${param.value}*${idx}`}
                                                                         style={{justifyContent: 'space-between'}}>
                                                                        <div className="d-flex flex-grow-1 align-items-center">
                                                                            <span className="font-monospace text-break d-block d-sm-inline-block"
                                                                                  data-bs-toggle="tooltip">
                                                                                <React.Fragment>
                                                                                    <span className="text-muted rounded me-1 text-truncate">{param.name}</span>:
                                                                                    <BTooltip text={`${param.name} (${param.type})`}>
                                                                                        <span className="font-monospace text-break d-block d-sm-inline-block">{param.value}</span>
                                                                                    </BTooltip>
                                                                                </React.Fragment>
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                        ) : (
                                                            <span className="font-monospace text-break">{ele?.data}</span>
                                                        )}
                                                        <div className="d-flex flex-column align-items-end mt-3">
                                                            <Form className="d-flex align-items-end mt-3 mt-md-0 ms-md-3">
                                                                <ButtonGroup>
                                                                    <ToggleButton
                                                                        id={`toggle-check-dec-${index}`}
                                                                        type="radio"
                                                                        name={`decHexOptions-${index}`}
                                                                        variant="secondary"
                                                                        className="btn btn-sm custom-btn"
                                                                        value="dec"
                                                                        checked={selectedOptions[index] === 'dec' || !selectedOptions[index]}
                                                                        onChange={(e) => handleOptionChange(index, 'dec')}
                                                                    >
                                                                        Dec
                                                                    </ToggleButton>
                                                                    <ToggleButton
                                                                        id={`toggle-check-hex-${index}`}
                                                                        type="radio"
                                                                        variant="secondary"
                                                                        name={`decHexOptions-${index}`}
                                                                        className="btn btn-sm custom-btn"
                                                                        value="hex"
                                                                        checked={selectedOptions[index] === 'hex'}
                                                                        onChange={(e) => handleOptionChange(index, 'hex')}
                                                                    >
                                                                        Hex
                                                                    </ToggleButton>
                                                                </ButtonGroup>
                                                            </Form>
                                                        </div>
                                                    </ul>
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                    {index < stats?.length - 1 ? <hr className="log-hr"/> : ""}
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default TransactionLogs;
